import React, { useEffect, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../contexts/AuthContext";
import SaveAsTemplateModal from "./SaveAsTemplateModal";
import GlobalItemSelectorModal from "./GlobalItemSelectorModal";
import FloatingActionButton from "../components/FloatingActionButton";
import { useConfirmDialog } from "../contexts/ConfirmDialogContext";

interface UserPacklistTabProps {
  tripId: string;
  userId: string;
}

interface UserPacklistItem {
  id: string;
  item_name: string;
  category: string;
  packed: boolean | null;
  is_custom: boolean | null;
}

interface TemplateItemWithJoin {
  item: {
    name: string;
    category: {
      name: string;
    };
  };
}

export default function UserPacklistTab({
  tripId,
  userId,
}: UserPacklistTabProps) {
  const { user } = useAuth();
  const [items, setItems] = useState<UserPacklistItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [templateOptions, setTemplateOptions] = useState<any[]>([]);
  const [newItemName, setNewItemName] = useState("");
  const [newItemCategory, setNewItemCategory] = useState("");
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [nameFilter, setNameFilter] = useState("");
  const [collapsedCategories, setCollapsedCategories] = useState<
    Record<string, boolean>
  >({});
  const [globalItems, setGlobalItems] = useState<any[]>([]);
  const [addingItemId, setAddingItemId] = useState<string | null>(null);
  const [showGlobalItemModal, setShowGlobalItemModal] = useState(false);
  const [saveSuccessMessage, setSaveSuccessMessage] = useState<string | null>(
    null
  );
  const [recentlyDeletedItemInfo, setRecentlyDeletedItemInfo] = useState<{
    id: string;
    item_name: string;
    category: string;
  } | null>(null);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [initialCollapsedCategories, setInitialCollapsedCategories] = useState<
    Record<string, boolean>
  >({});
  const searchActiveRef = React.useRef(false);
  const { showConfirm } = useConfirmDialog();
  const handleAddItem = async () => {
    if (!newItemName || !newItemCategory || !user) return;

    const { error } = await supabase.from("user_packlist_items").insert([
      {
        user_id: user.id,
        trip_id: tripId,
        item_name: newItemName.trim(),
        category: newItemCategory,
        packed: false,
        is_custom: true,
      },
    ]);

    if (error) {
      console.error("Fehler beim Hinzufügen des Items:", error.message);
      return;
    }

    setItems((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        item_name: newItemName.trim(),
        category: newItemCategory,
        packed: false,
        is_custom: true,
      },
    ]);

    setNewItemName("");
    setNewItemCategory("");
  };

  const addGlobalItem = async (itemId: string) => {
    const item = globalItems.find((i) => i.id === itemId);
    if (!item || !user) return;
    setAddingItemId(itemId);

    const category =
      categories.find((c) => c.id === item.category_id)?.name ?? "Unbekannt";

    const { error } = await supabase.from("user_packlist_items").insert([
      {
        user_id: user.id,
        trip_id: tripId,
        item_name: item.name,
        category,
        packed: false,
        is_custom: false,
      },
    ]);

    if (!error) {
      setItems((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          item_name: item.name,
          category,
          packed: false,
          is_custom: false,
        },
      ]);
      setGlobalItems((prev) => prev.filter((i) => i.id !== itemId));
    } else {
      console.error("Fehler beim Hinzufügen:", error.message);
    }

    setAddingItemId(null);
  };

  const togglePacked = async (itemId: string, currentStatus: boolean) => {
    const { error } = await supabase
      .from("user_packlist_items")
      .update({ packed: !currentStatus })
      .eq("id", itemId);

    if (error) {
      console.error("Fehler beim Aktualisieren des Packstatus:", error.message);
      return;
    }

    setItems((prev) =>
      prev.map((item) =>
        item.id === itemId ? { ...item, packed: !currentStatus } : item
      )
    );
  };

  const [categories, setCategories] = useState<{ id: string; name: string }[]>(
    []
  );

  const [hidePacked, setHidePacked] = useState(false);

  useEffect(() => {
    const active = nameFilter.trim().length > 0;

    if (active && !searchActiveRef.current) {
      // Suche beginnt – Zustand merken und alles aufklappen
      setInitialCollapsedCategories(collapsedCategories);
      searchActiveRef.current = true;

      const categoriesToExpand = Object.fromEntries(
        Object.keys(groupItemsByCategory(items)).map((cat) => [cat, false])
      );
      setCollapsedCategories(categoriesToExpand);
    }

    if (!active && searchActiveRef.current) {
      // Suche endet – ursprünglichen Zustand wiederherstellen
      setCollapsedCategories(initialCollapsedCategories);
      setInitialCollapsedCategories({});
      searchActiveRef.current = false;
    }
  }, [nameFilter]);

  useEffect(() => {
    if (items.length > 0 && Object.keys(collapsedCategories).length === 0) {
      const categories = Array.from(
        new Set(items.map((item) => item.category))
      );

      setCollapsedCategories(
        Object.fromEntries(categories.map((cat) => [cat, true]))
      );
    }
  }, [items, collapsedCategories]);

  useEffect(() => {
    if (!user?.id) return;
    // Falls du dort nochmal laden willst, z. B. initial, dann:
    loadTemplates();
  }, [user?.id]);

  // Templates wieder anzeigen
  const loadTemplates = async () => {
    if (!user?.id) return; // ⛔️ Kein User vorhanden? Dann abbrechen!

    const [
      { data: publicTemplates, error: pubErr },
      { data: privateTemplates, error: privErr },
    ] = await Promise.all([
      supabase.from("packlist_templates").select("id, name, description"),
      supabase
        .from("user_packlist_templates")
        .select("id, name, description")
        .eq("user_id", user.id),
    ]);

    if (pubErr || privErr) {
      console.error("Fehler beim Laden der Vorlagen:", pubErr || privErr);
    } else {
      const mergedTemplates = [
        ...(publicTemplates ?? []).map((t) => ({ ...t, source: "public" })),
        ...(privateTemplates ?? []).map((t) => ({ ...t, source: "private" })),
      ];
      setTemplateOptions(mergedTemplates);
    }
  };

  const handleDeleteItem = async (itemId: string, itemName: string) => {
    const { error } = await supabase
      .from("user_packlist_items")
      .delete()
      .eq("id", itemId);

    if (error) {
      console.error("Fehler beim Löschen des Items:", error.message);
      return;
    }

    setItems((prev) => {
      const deletedItem = prev.find((item) => item.id === itemId);
      if (deletedItem) {
        setRecentlyDeletedItemInfo({
          id: deletedItem.id,
          item_name: deletedItem.item_name,
          category: deletedItem.category,
        });
        setTimeout(() => setRecentlyDeletedItemInfo(null), 3000);
      }

      const updated = prev.filter((item) => item.id !== itemId);
      if (updated.length === 0) {
        loadTemplates();
      }
      return updated;
    });
  };

  const groupItemsByCategory = (items: UserPacklistItem[]) => {
    const groups: Record<string, UserPacklistItem[]> = {};

    items
      .filter((item) =>
        item.item_name.toLowerCase().includes(nameFilter.trim().toLowerCase())
      )
      .forEach((item) => {
        if (!groups[item.category]) {
          groups[item.category] = [];
        }
        groups[item.category].push(item);
      });

    // Sortiere innerhalb jeder Kategorie alphabetisch nach item_name
    Object.keys(groups).forEach((category) => {
      groups[category].sort((a, b) =>
        a.item_name.localeCompare(b.item_name, "de", { sensitivity: "base" })
      );
    });

    return groups;
  };

  useEffect(() => {
    if (nameFilter.trim().length > 0) {
      // Alle Kategorien aufklappen, wenn Suchbegriff aktiv ist
      const categoriesToExpand = Object.fromEntries(
        Object.keys(groupItemsByCategory(items)).map((cat) => [cat, false])
      );
      setCollapsedCategories(categoriesToExpand);
    }
  }, [nameFilter]);

  useEffect(() => {
    const loadCategories = async () => {
      const { data, error } = await supabase
        .from("packlist_categories")
        .select("id, name");

      if (error) {
        console.error("Fehler beim Laden der Kategorien:", error.message);
      } else {
        setCategories(data || []);
      }
    };

    loadCategories();
  }, []);

  useEffect(() => {
    const loadItems = async () => {
      if (!userId || !tripId) return;

      setLoading(true);
      const { data: userItems, error: itemsError } = await supabase
        .from("user_packlist_items")
        .select("id, item_name, category, packed, is_custom")
        .eq("user_id", userId)
        .eq("trip_id", tripId);

      if (itemsError) {
        console.error("Fehler beim Laden der Packliste:", itemsError.message);
        setLoading(false);
        return;
      }

      // ✅ HIERHER verschieben:
      const { data: allItems, error: globalError } = await supabase
        .from("packlist_items")
        .select("id, name, category_id, created_at");

      if (globalError) {
        console.error("Fehler beim Laden globaler Items:", globalError.message);
      }

      const userItemNames = new Set((userItems ?? []).map((i) => i.item_name));
      const filteredGlobal = (allItems ?? []).filter(
        (item) => !userItemNames.has(item.name)
      );
      setGlobalItems(filteredGlobal);

      if ((userItems?.length ?? 0) === 0) {
        const [
          { data: publicTemplates, error: pubErr },
          { data: privateTemplates, error: privErr },
        ] = await Promise.all([
          supabase.from("packlist_templates").select("id, name, description"),
          supabase
            .from("user_packlist_templates")
            .select("id, name, description")
            .eq("user_id", user?.id ?? ""),
        ]);

        if (pubErr || privErr) {
          console.error("Fehler beim Laden der Vorlagen:", pubErr || privErr);
        } else {
          const mergedTemplates = [
            ...(publicTemplates ?? []).map((t) => ({ ...t, source: "public" })),
            ...(privateTemplates ?? []).map((t) => ({
              ...t,
              source: "private",
            })),
          ];
          setTemplateOptions(mergedTemplates);
        }
      } else {
        setItems(userItems || []);
      }

      setLoading(false);
    };

    loadItems(); // ✅ korrekt aufrufen
  }, [tripId, userId]);

  const handleSelectTemplate = async (
    templateId: string,
    source: "public" | "private"
  ) => {
    if (!user || !tripId) return;

    let result;
    if (source === "public") {
      result = await supabase
        .from("packlist_template_items")
        .select("item:packlist_items(name, category:packlist_categories(name))")
        .eq("template_id", templateId);
    } else {
      result = await supabase
        .from("user_packlist_template_items")
        .select("item_name, category_id")
        .eq("template_id", templateId);
    }

    if (result.error) {
      console.error(
        "Fehler beim Laden der Template-Items:",
        result.error.message
      );
      return;
    }

    const rawItems: any[] = result.data ?? [];

    const inserts =
      source === "public"
        ? rawItems
            .filter((entry: any) => entry.item && !Array.isArray(entry.item))
            .map((entry: any) => ({
              user_id: user.id,
              trip_id: tripId,
              item_name: entry.item.name,
              category: entry.item.category?.name ?? "Unbekannt",
              packed: false,
              is_custom: false,
            }))
        : rawItems.map((entry: any) => {
            const categoryName =
              categories.find((c) => c.id === entry.category_id)?.name ??
              "Unbekannt";
            return {
              user_id: user.id,
              trip_id: tripId,
              item_name: entry.item_name,
              category: categoryName, // <-- das ist der string, den du brauchst
              packed: false,
              is_custom: false,
            };
          });

    const { error: insertError } = await supabase
      .from("user_packlist_items")
      .insert(inserts);

    if (insertError) {
      console.error("Fehler beim Anlegen der Packliste:", insertError.message);
      return;
    }

    // Danach neu laden
    const { data: newItems } = await supabase
      .from("user_packlist_items")
      .select("*")
      .eq("user_id", user.id)
      .eq("trip_id", tripId);

    setItems(newItems || []);
    setTemplateOptions([]); // Vorlagen ausblenden
  };

  const handleDeleteTemplate = async (templateId: string) => {
    const confirmed = await showConfirm({
      title: "Vorlage löschen",
      message: "Möchtest du diese Vorlage wirklich löschen? ",
      confirmLabel: "Löschen",
      cancelLabel: "Abbrechen",
      icon: "warning",
    });

    if (!confirmed) return;
    if (!user?.id) return;

    const { error } = await supabase
      .from("user_packlist_templates")
      .delete()
      .eq("id", templateId)
      .eq("user_id", user.id);

    if (error) {
      console.error("Fehler beim Löschen der Vorlage:", error.message);
      return;
    }

    // Aus der Anzeige entfernen
    setTemplateOptions((prev) => prev.filter((tpl) => tpl.id !== templateId));
  };

  const visibleItems = hidePacked ? items.filter((i) => !i.packed) : items;
  const groupedItems = groupItemsByCategory(visibleItems);

  const groupedGlobalItems = React.useMemo(() => {
    const usedNames = new Set(
      items.map((i) => i.item_name.trim().toLowerCase())
    );

    const filtered = globalItems.filter(
      (item) => !usedNames.has(item.name.trim().toLowerCase())
    );

    const grouped = filtered.reduce((acc: Record<string, any[]>, item) => {
      const category =
        categories.find((c) => c.id === item.category_id)?.name ?? "Unbekannt";
      if (!acc[category]) acc[category] = [];
      acc[category].push(item);
      return acc;
    }, {});

    return Object.entries(grouped);
  }, [items, globalItems, categories]);

  return (
    <div className="mt-4">
      <h3 className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-5 px-1">
        Meine Packliste
      </h3>

      {!loading && items.length === 0 && templateOptions.length > 0 && (
        <p className="text-slate-500 text-sm mb-4 px-1">
          Wähle eine Vorlage für deine Packliste aus.
        </p>
      )}

      {loading ? (
        <p className="text-slate-600 text-sm px-1">Lade Packliste…</p>
      ) : items.length === 0 && templateOptions.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {templateOptions.map((template) => (
            <div
              key={template.id}
              className="relative bg-[#161b27] rounded-xl border border-[#2a3347] p-4 cursor-pointer hover:border-sky-500/20 transition-colors"
              onClick={() => handleSelectTemplate(template.id, template.source)}
            >
              {template.source === "private" && (
                <button
                  className="absolute top-3 right-3 text-slate-600 hover:text-red-400 transition-colors z-10"
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    handleDeleteTemplate(template.id);
                  }}
                  title="Vorlage löschen"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
              <h3 className="text-sm font-semibold text-slate-100 mb-1 pr-6">
                {template.name}
              </h3>
              <p className="text-xs text-slate-400">{template.description}</p>
            </div>
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-10">
          <p className="text-slate-500 text-sm">Noch keine Packlisten-Einträge vorhanden.</p>
        </div>
      ) : (
        <>
          {/* Modal: Gegenstand hinzufügen */}
          {showAddItemModal && (
            <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center">
              <div className="bg-[#161b27] rounded-2xl border border-[#2a3347] w-full max-w-md p-6 relative">
                <button
                  className="absolute top-3 right-4 text-slate-600 hover:text-slate-300 text-xl transition-colors"
                  onClick={() => setShowAddItemModal(false)}
                >
                  ✕
                </button>
                <h3 className="text-sm font-semibold text-slate-100 mb-5">
                  Gegenstand hinzufügen
                </h3>
                <button
                  onClick={() => {
                    setShowGlobalItemModal(true);
                    setShowAddItemModal(false);
                  }}
                  className="text-xs text-sky-400 hover:text-sky-300 transition-colors mb-5 block"
                >
                  Aus Bibliothek auswählen
                </button>
                <div className="flex flex-col gap-3">
                  <input
                    type="text"
                    placeholder="Name des Gegenstands"
                    value={newItemName}
                    onChange={(e) => setNewItemName(e.target.value)}
                    className="w-full bg-[#1e2639] border border-[#2a3347] rounded-xl text-slate-100 px-3 py-2 text-sm focus:outline-none focus:border-sky-500/50 placeholder:text-slate-600"
                  />
                  <select
                    value={newItemCategory}
                    onChange={(e) => setNewItemCategory(e.target.value)}
                    className="w-full bg-[#1e2639] border border-[#2a3347] rounded-xl text-slate-100 px-3 py-2 text-sm focus:outline-none focus:border-sky-500/50"
                  >
                    <option value="">Kategorie wählen</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.name}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                <div className="mt-5 flex justify-end gap-2">
                  <button
                    onClick={() => setShowAddItemModal(false)}
                    className="text-xs px-3 py-1.5 rounded-full bg-[#1e2639] text-slate-400 hover:text-slate-200 transition-colors"
                  >
                    Abbrechen
                  </button>
                  <button
                    onClick={async () => {
                      await handleAddItem();
                      setShowAddItemModal(false);
                    }}
                    className="text-xs px-3 py-1.5 rounded-full bg-sky-600 text-white hover:bg-sky-500 transition-colors"
                  >
                    Hinzufügen
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Toolbar: Suche + Filter + Expand */}
          <div className="flex flex-wrap items-center gap-2 mb-5">
            <div className="relative flex-1 min-w-[140px]">
              <input
                type="text"
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
                placeholder="Suchen…"
                className="w-full bg-[#1e2639] border border-[#2a3347] rounded-xl text-slate-100 px-3 py-1.5 text-xs focus:outline-none focus:border-sky-500/50 placeholder:text-slate-600 pr-7"
              />
              {nameFilter.length > 0 && (
                <button
                  onClick={() => setNameFilter("")}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-600 hover:text-slate-300 text-sm"
                  aria-label="Suche zurücksetzen"
                >
                  ✕
                </button>
              )}
            </div>

            <button
              onClick={() => setHidePacked(!hidePacked)}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-colors shrink-0 ${
                hidePacked
                  ? "bg-sky-500/20 text-sky-400 border border-sky-500/30"
                  : "bg-[#1e2639] text-slate-400 hover:text-slate-200"
              }`}
            >
              🧳 Gepackte ausblenden
              {hidePacked && <span className="opacity-50 ml-0.5">✕</span>}
              {items.length > 0 && (
                <span className="opacity-50 ml-1 text-[10px]">
                  ({items.filter((i) => i.packed).length}/{items.length})
                </span>
              )}
            </button>

            <button
              onClick={() =>
                setCollapsedCategories(
                  Object.fromEntries(Object.keys(groupedItems).map((cat) => [cat, false]))
                )
              }
              className="text-xs text-sky-400 hover:text-sky-300 transition-colors shrink-0"
            >
              Alle öffnen
            </button>
            <button
              onClick={() =>
                setCollapsedCategories(
                  Object.fromEntries(Object.keys(groupedItems).map((cat) => [cat, true]))
                )
              }
              className="text-xs text-sky-400 hover:text-sky-300 transition-colors shrink-0"
            >
              Alle schließen
            </button>
          </div>

          {/* Kategorien + Items */}
          <div className="space-y-5">
            {Object.entries(groupedItems).map(([categoryName, categoryItems]) => {
              const isCollapsed = collapsedCategories[categoryName] ?? false;
              const packedCount = categoryItems.filter((i) => i.packed).length;

              return (
                <div key={categoryName}>
                  {/* Kategorie-Header */}
                  <button
                    className="w-full flex items-center gap-2 py-2 mb-1"
                    onClick={() =>
                      setCollapsedCategories((prev) => ({
                        ...prev,
                        [categoryName]: !prev[categoryName],
                      }))
                    }
                  >
                    <span className="text-sm uppercase tracking-widest text-slate-600 shrink-0">
                      {categoryName}
                    </span>
                    <span className={`text-[10px] shrink-0 tabular-nums ${
                      packedCount === categoryItems.length ? "text-emerald-500/50" : "text-slate-600"
                    }`}>
                      {packedCount}/{categoryItems.length}
                    </span>
                    <div className="flex-1 h-px bg-[#2a3347]" />
                    <span className="text-slate-600 text-[10px] shrink-0">
                      {isCollapsed ? "▲" : "▼"}
                    </span>
                  </button>

                  {/* Fortschrittsbalken */}
                  <div className="h-0.5 bg-[#1e2639] rounded-full mb-2 overflow-hidden">
                    <div
                      className="h-full bg-emerald-500/40 rounded-full transition-all duration-500"
                      style={{ width: `${Math.round((packedCount / categoryItems.length) * 100)}%` }}
                    />
                  </div>

                  {/* Items */}
                  {!isCollapsed && (
                    <div className="bg-[#161b27] rounded-xl border border-[#2a3347] px-4 py-1">
                      {categoryItems.map((item, idx) => (
                        <React.Fragment key={item.id}>
                          <div
                            className={`flex items-center gap-3 py-3.5 cursor-pointer select-none ${
                              idx < categoryItems.length - 1 ? "border-b border-[#1e2639]" : ""
                            }`}
                            translate="no"
                            onClick={(e) => {
                              if ((e.target as HTMLElement).closest("button")) return;
                              togglePacked(item.id, item.packed ?? false);
                            }}
                          >
                            <span className={`text-[10px] shrink-0 ${item.packed ? "text-emerald-400" : "text-slate-600"}`}>
                              {item.packed ? "✓" : "○"}
                            </span>
                            <span
                              className={`text-xs flex-1 ${item.packed ? "text-slate-500 line-through" : "text-slate-300"}`}
                              translate="no"
                              role="text"
                            >
                              {item.item_name}
                            </span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteItem(item.id, item.item_name);
                              }}
                              className="text-slate-700 hover:text-red-400 transition-colors shrink-0"
                              title="Item löschen"
                              type="button"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        </React.Fragment>
                      ))}
                      {recentlyDeletedItemInfo && recentlyDeletedItemInfo.category === categoryName && (
                        <div className="py-2 text-[11px] text-emerald-400/70 italic">
                          „{recentlyDeletedItemInfo.item_name}" entfernt
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Bottom actions */}
      {items.length > 0 && (
        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <button
            onClick={async () => {
              const confirmed = await showConfirm({
                title: "Packliste zurücksetzen",
                message: "Möchtest du wirklich alle Packlisten-Einträge für diese Reise löschen?",
                confirmLabel: "Ja, löschen",
                cancelLabel: "Abbrechen",
                icon: "warning",
              });
              if (!confirmed || !user) return;
              const { error } = await supabase
                .from("user_packlist_items")
                .delete()
                .eq("user_id", user.id)
                .eq("trip_id", tripId);
              if (error) {
                console.error("Fehler beim Zurücksetzen der Liste:", error.message);
                return;
              }
              setItems([]);
              await loadTemplates();
            }}
            className="text-[11px] text-slate-600 hover:text-red-400 transition-colors"
          >
            Alle löschen
          </button>
          <div className="flex flex-col items-end gap-1">
            <button
              onClick={() => setShowSaveModal(true)}
              className="text-[11px] text-sky-400 hover:text-sky-300 transition-colors"
            >
              Als Vorlage speichern
            </button>
            {saveSuccessMessage && (
              <div className="text-[11px] text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-1.5 max-w-xs text-right">
                {saveSuccessMessage}
              </div>
            )}
          </div>
        </div>
      )}

      <SaveAsTemplateModal
        open={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        tripId={tripId}
        onSuccess={() => {
          setSaveSuccessMessage(
            "✅ Vorlage gespeichert und kann beim nächsten Trip ausgewählt werden."
          );
          setTimeout(() => setSaveSuccessMessage(null), 6000);
        }}
      />
      <GlobalItemSelectorModal
        open={showGlobalItemModal}
        onClose={() => setShowGlobalItemModal(false)}
        groupedGlobalItems={groupedGlobalItems}
        addingItemId={addingItemId}
        onAddItem={addGlobalItem}
      />
      {items.length > 0 && (
        <FloatingActionButton
          visible={true}
          localStorageKey="seen_fab_packlist"
          actions={[
            {
              label: "Neues Item",
              onClick: () => setShowAddItemModal(true),
            },
          ]}
        />
      )}
    </div>
  );
}
